import React from 'react'
import * as signalr from '@microsoft/signalr'
import { HubConnectionState } from '@microsoft/signalr'
import useAuth from './useAuth'
import { HubReceive } from 'enums/HubMethods'

type Status = 'connecting' | 'connected' | 'disconnected' | 'error'

type Method<a,r> = [string,(_:a) => r]
type Method_ = <R>(cb:<a,r>(_:Method<a,r>) => R) => R
export const mkMethod = <a,r>(x:Method<a,r>) : Method_ => cb => cb(x)

type Invoke = <a,r>(method:string,args:unknown[],handler:(_:a) => r) => Promise<r>

let _token : string | undefined = undefined
let _status : Status = 'connecting'
let _connection : Promise<signalr.HubConnection> | undefined = undefined

const setToken = async (connection:signalr.HubConnection,token:string) : Promise<signalr.HubConnection> => {
  return _connection = (async () => {
    await connection.invoke('SetToken',token)
    return connection
  })()
}

const connect = async (setAuth:(_:string) => void,setStatus:(_:Status) => void,connection:signalr.HubConnection,delay?:number) : Promise<signalr.HubConnection> => {
  try
  {
    if(!!delay)
    {
      console.log('ws.connect',delay)
      await new Promise((resolve) => window.setTimeout(resolve,delay))
    }
    connection.on(HubReceive.Token,(x:string) => setAuth(x))
    await connection.start()
    if(connection.state === HubConnectionState.Connected)
    {
      setStatus(_status = 'connected')
      return setToken(connection,_token!)
    }
    else
    {
      throw 'ws.connect - not connected'
    }
  }
  catch(e)
  {
    if(!!delay && delay > 60000)
    {
      console.error('useSignalR.connect - error',e)
      setStatus('error')
      throw e
    }
    else
    {
      return connect(setAuth,setStatus,connection,delay ? delay * 2 : 1000)
    }
  }
}

const connection = async (token:string,setAuth:(_:string) => void,setStatus:(_:Status) => void) : Promise<signalr.HubConnection> => {
  console.log('ws.connection',_status)
  if(_connection)
  {
    console.log('ws.connection','existing')
    if(token === _token)
    {
      console.log('ws.connection','token - matched')
      return _connection
    }
    else
    {
      console.log('ws.connection','token - changed')
      return setToken(await _connection,token)
    }
  }
  else
  {
    console.log('ws.connection','new')
    _token = token
    const c = new signalr.HubConnectionBuilder().withUrl(`/hub`).configureLogging(signalr.LogLevel.Debug).build()
    c.onclose(() => {
      console.log('ws','close')
      setStatus(_status = 'disconnected')
      _connection = connect(setAuth,setStatus,c,0)
    })
    return _connection = connect(setAuth,setStatus,c)
  }
}

export default (methods:Method_[]) : [Invoke,Status] => {
  const [auth,setAuth] = useAuth()
  const [status,setStatus] = React.useState<Status>(_status)
  const unbind = async (connection:Promise<signalR.HubConnection>) => {
    const c = await connection
    methods.forEach((m:Method_) =>
      m(([method,f]) => c.off(method,f))
    )
  }
  const bind = async (connection:Promise<signalR.HubConnection>) => {
    const c = await connection
    await unbind(connection)
    methods.forEach((m:Method_) =>
      m(([method,f]) => c.on(method,f))
    )
  }
  React.useEffect(() => {
    if(!!auth.token)
    {
      const c = connection(auth.token,setAuth,setStatus)
      bind(c)
      return () => { unbind(c) }
    }
    else
    {
      console.log('useSignalR invoke - no auth')
      window.location.reload() // TODO: actually fix this
    }
  },[auth])
  return [
    // async (method,...args) => {
    //   return (await connection(auth.token,setStatus)).invoke(method,...args)
    // },
    async (method,args,handler) => {
      if(!!auth.token)
      {
        const c = await connection(auth.token,setAuth,setStatus)
        const result = await c.invoke(method,...args)
        return handler(result)
      }
      else
      {
        console.log('useSignalR invoke - no auth')
        window.location.reload() // TODO: actually fix this
        return null as any
      }
    },
    status,
  ]
}
